import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, Link2, TriangleAlert } from "lucide-react";
import { ApiError, downloadLandResearchReport, landDataApi, type LandDataView } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { cn } from "@/lib/cn";
import { EvidenceContext, EvidenceDrawer, type EvidenceItem } from "./land-data/evidence";
import { MetricCompareSection } from "./land-data/metrics";
import { CitySection, DistrictDataSection } from "./land-data/regiondata";
import { GapsPanel, DOMAINS, KpiStrip } from "./land-data/overview";
import { RegionSection } from "./land-data/region";
import { EnterpriseSection, LandSupplySection, LocationSection, RoadsSection } from "./land-data/sections";
import { Lightbox, LightboxContext } from "./land-data/shared";

/* -------------------------------------------------------------------------- */
/* navigation — left sticky list on wide screens, pill bar on narrow ones      */
/* -------------------------------------------------------------------------- */

interface NavItem {
  id: string;
  label: string;
}

function useActiveSection(items: NavItem[]): [string, (id: string) => void] {
  const [active, setActive] = useState(items[0]?.id ?? "");
  const ids = items.map((it) => it.id).join(",");
  useEffect(() => {
    const targets = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el != null);
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-16px 0px -65% 0px", threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);
  return [active, setActive];
}

function jump(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SideNav({ items }: { items: NavItem[] }): JSX.Element {
  const [active, setActive] = useActiveSection(items);
  const go = (id: string): void => {
    jump(id);
    setActive(id);
  };
  return (
    <>
      {/* narrow: horizontal pills */}
      <nav className="sticky top-0 z-20 -mx-4 border-b border-cardline bg-canvas/90 px-4 py-2 backdrop-blur lg:hidden">
        <div className="flex gap-1.5 overflow-x-auto">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => go(it.id)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-medium",
                active === it.id ? "bg-brand-900 text-white" : "bg-white text-zinc-600",
              )}
            >
              {it.label}
            </button>
          ))}
        </div>
      </nav>
      {/* wide: sticky side list */}
      <nav className="sticky top-6 hidden self-start lg:block">
        <div className="rounded-xl border border-cardline bg-white p-2 shadow-card">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => go(it.id)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] transition-colors",
                active === it.id ? "bg-brand-50 font-semibold text-brand-900" : "text-zinc-600 hover:bg-canvas",
              )}
            >
              <span className="flex-1">{it.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* page                                                                        */
/* -------------------------------------------------------------------------- */

export function LandDataPage(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const { convId = "" } = useParams();
  const [data, setData] = useState<LandDataView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [shot, setShot] = useState<{ src: string; caption?: string } | null>(null);
  const [evidenceItem, setEvidenceItem] = useState<EvidenceItem | null>(null);
  const [copied, setCopied] = useState(false);

  const openLightbox = useCallback((src: string, caption?: string) => setShot({ src, caption }), []);
  const lightboxValue = useMemo(() => ({ open: openLightbox }), [openLightbox]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    landDataApi
      .view(session.token, convId, ctrl.signal)
      .then((v) => setData(v))
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "数据加载失败");
      })
      .finally(() => !ctrl.signal.aborted && setLoading(false));
    return () => ctrl.abort();
  }, [session.token, convId]);

  const generatedAt = useMemo(() => {
    const ts = data?.project.generated_at;
    if (!ts) return "";
    return new Date(ts * 1000).toLocaleString("zh-CN", { hour12: false });
  }, [data]);

  const evidenceValue = useMemo(
    () => ({ open: (item: EvidenceItem) => setEvidenceItem(item), searchedAt: generatedAt }),
    [generatedAt],
  );

  const navItems = useMemo<NavItem[]>(() => {
    if (!data) return [];
    const present: Record<string, boolean> = {
      location: data.location.dims.length > 0 || data.location.maps.length > 0,
      roads: data.roads.roads.length > 0,
      enterprise: data.enterprise != null,
      city: !!data.region_data?.city_profile && Object.keys(data.region_data.city_profile).length > 0,
      districts: data.districts.length > 0 || (data.region_data?.districts.length ?? 0) > 0,
      zones: data.zones.length > 0,
      land: data.land_supply != null,
      region: data.region != null && data.region.status !== "skipped",
    };
    const items: NavItem[] = [{ id: "sec-overview", label: "概览" }];
    for (const d of DOMAINS) {
      if (!present[d.key]) continue;
      items.push({ id: d.id, label: d.label });
    }
    if (data.gaps?.length) items.push({ id: "sec-gaps", label: "数据缺口" });
    return items;
  }, [data]);

  const onDownload = (): void => {
    void downloadLandResearchReport(session.token, convId, data?.download_name || "地块数据.xlsx");
  };
  const onCopyLink = (): void => {
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <LightboxContext.Provider value={lightboxValue}>
      <EvidenceContext.Provider value={evidenceValue}>
        <div className="h-full overflow-y-auto bg-canvas">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
            <header className="mb-4 flex items-center gap-2 text-[13px] text-zinc-500">
              <button onClick={() => navigate("/land")} className="rounded-md p-1.5 hover:bg-zinc-100" title="返回">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <span>地块数据</span>
              <span className="text-zinc-300">/</span>
              <span className="text-zinc-800">数据详情</span>
            </header>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-40 w-full animate-pulse rounded-xl bg-white shadow-card" />
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 rounded-lg border border-bad/30 bg-bad/5 px-5 py-4 text-[14px] text-bad">
                <TriangleAlert className="h-4 w-4" />
                {error}
              </div>
            ) : data ? (
              <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[168px_minmax(0,1fr)]">
                <SideNav items={navItems} />
                <main className="min-w-0 space-y-5">
                  <div id="sec-overview" className="scroll-mt-4 space-y-4">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-[16rem] flex-1">
                        <h1 className="text-[22px] font-semibold tracking-tight text-brand-900">
                          {data.project.name || "地块数据"}
                        </h1>
                        <p className="mt-0.5 text-[13px] text-zinc-500">
                          {[data.project.city, data.project.district].filter(Boolean).join(" · ")}
                          {generatedAt ? ` · 搜索于 ${generatedAt}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          onClick={onCopyLink}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-cardline bg-white px-3 py-2 text-[13px] text-zinc-700 hover:bg-brand-50"
                        >
                          {copied ? <Check className="h-4 w-4 text-good" /> : <Link2 className="h-4 w-4" />}
                          {copied ? "已复制" : "复制链接"}
                        </button>
                        {data.download_name ? (
                          <button
                            onClick={onDownload}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-900 px-3.5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-800"
                          >
                            <Download className="h-4 w-4" />
                            下载数据 Excel
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <KpiStrip data={data} />
                    <p className="text-[12px] text-zinc-400">
                      点击任意数值可查看来源、口径与截图。
                    </p>
                  </div>

                  <CitySection data={data} />
                  <DistrictDataSection data={data} />
                  <RegionSection data={data} />
                  <MetricCompareSection data={data} kind="zones" />
                  <LandSupplySection data={data} />
                  <EnterpriseSection data={data} />
                  <RoadsSection data={data} />
                  <LocationSection data={data} />
                  <GapsPanel data={data} />
                </main>
              </div>
            ) : null}
          </div>
          <EvidenceDrawer item={evidenceItem} searchedAt={generatedAt} onClose={() => setEvidenceItem(null)} />
          <Lightbox shot={shot} onClose={() => setShot(null)} />
        </div>
      </EvidenceContext.Provider>
    </LightboxContext.Provider>
  );
}
